/**
 * TanStack Query hooks for Deals
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type { DealCreate, DealUpdate } from '../types';

// Query keys
export const dealKeys = {
  all: ['deals'] as const,
  lists: () => [...dealKeys.all, 'list'] as const,
  list: (filters: { includeArchived?: boolean }) => [...dealKeys.lists(), filters] as const,
  details: () => [...dealKeys.all, 'detail'] as const,
  detail: (id: number) => [...dealKeys.details(), id] as const,
};

// Fetch all deals
export function useDeals(includeArchived = false) {
  return useQuery({
    queryKey: dealKeys.list({ includeArchived }),
    queryFn: () => api.deals.list(includeArchived),
    staleTime: 30 * 1000, // 30 seconds
  });
}

// Fetch a single deal
export function useDeal(dealId: number) {
  return useQuery({
    queryKey: dealKeys.detail(dealId),
    queryFn: () => api.deals.get(dealId),
    enabled: !!dealId,
  });
}

// Create a deal
export function useCreateDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DealCreate) => api.deals.create(data),
    onSuccess: () => {
      // Invalidate deals list
      queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

// Update a deal
export function useUpdateDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ dealId, data }: { dealId: number; data: DealUpdate }) =>
      api.deals.update(dealId, data),
    onSuccess: (updatedDeal) => {
      // Update the deal in cache
      queryClient.setQueryData(dealKeys.detail(updatedDeal.id), updatedDeal);
      // Invalidate list to reflect changes
      queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

// Delete a deal
export function useDeleteDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dealId: number) => api.deals.delete(dealId),
    onSuccess: (_, dealId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: dealKeys.detail(dealId) });
      // Invalidate list
      queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

// Archive a deal
export function useArchiveDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dealId: number) => api.deals.archive(dealId),
    onSuccess: (updatedDeal) => {
      queryClient.setQueryData(dealKeys.detail(updatedDeal.id), updatedDeal);
      queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

// Unarchive a deal
export function useUnarchiveDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dealId: number) => api.deals.unarchive(dealId),
    onSuccess: (updatedDeal) => {
      queryClient.setQueryData(dealKeys.detail(updatedDeal.id), updatedDeal);
      queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}

// Reorder deals
export function useReorderDeals() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (dealIds: number[]) => api.deals.reorder(dealIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
    },
  });
}
